import React from 'react';
import { Link } from 'react-router-dom';
import Icon from './AppIcon';

const Footer = () => {
  const currentYear = new Date()?.getFullYear();

  const quickLinks = [
    { path: '/homepage', label: 'Home' },
    { path: '/menu-showcase', label: 'Menu' },
    { path: '/catering-excellence', label: 'Catering' },
    { path: '/our-italian-story', label: 'Our Story' }
  ];

  const contactInfo = [
    { icon: 'MapPin', text: '123 Etobicoke Street, Toronto, ON M9C 1A1' },
    { icon: 'Phone', text: '(416) 555-0123' },
    { icon: 'Mail', text: 'hello@montesantobakery.com' },
    { icon: 'Clock', text: 'Mon-Sat: 7am-7pm | Sun: 8am-5pm' }
  ];

  const socialLinks = [
    { icon: 'Instagram', url: 'https://instagram.com', label: 'Instagram' },
    { icon: 'Facebook', url: 'https://facebook.com', label: 'Facebook' },
    { icon: 'Twitter', url: 'https://twitter.com', label: 'Twitter' },
    { icon: 'Youtube', url: 'https://youtube.com', label: 'YouTube' }
  ];

  return (
    <footer className="bg-card border-t border-border">
      <div className="container-custom py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Croissant" size={28} color="var(--color-primary)" />
              </div>
              <div className="flex flex-col">
                <span className="font-accent text-2xl text-primary leading-none">
                  Montesanto
                </span>
                <span className="text-xs text-muted-foreground tracking-wider uppercase">
                  Artisan Bakery
                </span>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Authentic Italian baking traditions crafted with passion and precision since 1985.
            </p>
            <div className="flex gap-3">
              {socialLinks?.map((social) => (
                <a
                  key={social?.label}
                  href={social?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-all duration-300"
                  aria-label={social?.label}
                >
                  <Icon name={social?.icon} size={18} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-serif text-lg font-semibold text-foreground mb-4">
              Quick Links
            </h3>
            <ul className="space-y-2">
              {quickLinks?.map((link) => (
                <li key={link?.path}>
                  <Link
                    to={link?.path}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300 flex items-center gap-2 group"
                  >
                    <Icon 
                      name="ChevronRight" 
                      size={14} 
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                    {link?.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/visit-connect"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300 flex items-center gap-2 group"
                >
                  <Icon 
                    name="ChevronRight" 
                    size={14} 
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                  Visit Us
                </Link>
              </li>
              <li>
                <Link
                  to="/gallery-reviews"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300 flex items-center gap-2 group"
                >
                  <Icon 
                    name="ChevronRight" 
                    size={14} 
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                  Gallery & Reviews
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-serif text-lg font-semibold text-foreground mb-4">
              Contact Us
            </h3>
            <ul className="space-y-3">
              {contactInfo?.map((info, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name={info?.icon} size={16} color="var(--color-primary)" />
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {info?.text}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-serif text-lg font-semibold text-foreground mb-4">
              Newsletter
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Subscribe for daily specials, seasonal treats, and exclusive offers.
            </p>
            <form className="space-y-3" onSubmit={(e) => e?.preventDefault()}>
              <input
                type="email"
                placeholder="Your email address"
                className="w-full px-4 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300"
              />
              <button
                type="submit"
                className="w-full px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Icon name="Send" size={16} />
                Subscribe
              </button>
            </form>
          </div>
        </div>

        <div className="border-t border-border pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground text-center md:text-left">
              © {currentYear} Montesanto Bakery. All rights reserved. Crafted with love in Etobicoke.
            </p>
            <div className="flex gap-6">
              <Link
                to="/privacy"
                className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300"
              >
                Terms of Service
              </Link>
              <Link
                to="/accessibility"
                className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300"
              >
                Accessibility
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;